import { motion } from "framer-motion";
import { ReactNode } from "react";

interface Props {
  eyebrow?: string;
  /** Roman numeral index, e.g. "I", "II", "III" */
  index?: string;
  accent: string;
  rest: string;
  subtitle?: string;
  align?: "center" | "left";
  invert?: boolean;
  children?: ReactNode;
}

/**
 * Editorial bookplate heading — Roman numeral index, eyebrow with hairline rule,
 * italic accent + roman serif title. Inspired by engraved title-page typography.
 */
const SectionTitle = ({
  eyebrow,
  index,
  accent,
  rest,
  subtitle,
  align = "center",
  invert = false,
  children,
}: Props) => {
  const alignClass = align === "center" ? "text-center mx-auto" : "text-left";
  const eyebrowClass = invert ? "text-primary-foreground/70" : "text-muted-foreground";
  const restClass = invert ? "text-primary-foreground" : "text-foreground";
  const subClass = invert ? "text-primary-foreground/80" : "text-muted-foreground";
  const ruleClass = invert ? "bg-primary-foreground/40" : "bg-primary/60";
  const indexClass = invert ? "text-primary-foreground/70" : "text-accent";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className={`${alignClass} max-w-3xl`}
    >
      {index && (
        <p
          className={`${indexClass} text-sm italic mb-3`}
          style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.05em" }}
        >
          {index}.
        </p>
      )}
      {eyebrow && (
        <div className={`flex items-center gap-3 mb-4 ${align === "center" ? "justify-center" : ""}`}>
          <span className={`h-px w-10 ${ruleClass}`} aria-hidden="true" />
          <p className={`${eyebrowClass} text-[0.6875rem] tracking-[0.35em] uppercase`}>
            {eyebrow}
          </p>
          <span className={`h-px w-10 ${ruleClass} ${align === "left" ? "hidden" : ""}`} aria-hidden="true" />
        </div>
      )}
      <h2
        className={`text-3xl sm:text-4xl md:text-5xl leading-tight ${restClass}`}
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        <span className={`italic font-normal ${invert ? "text-primary-foreground/80" : "text-accent"}`}>{accent}</span>{" "}
        <span className="font-medium">{rest}</span>
      </h2>
      {subtitle && (
        <p className={`${subClass} mt-4 text-sm sm:text-base max-w-xl leading-relaxed ${align === "center" ? "mx-auto" : ""}`}>
          {subtitle}
        </p>
      )}
      {children}
    </motion.div>
  );
};

export default SectionTitle;

